import React from 'react'
import { FieldWrapper } from '@progress/kendo-react-form'
import {
  Input,
  MaskedTextBox,
  Checkbox,
  Switch,
  TextArea,
  NumericTextBox,
} from '@progress/kendo-react-inputs'
import { DatePicker, DateTimePicker } from '@progress/kendo-react-dateinputs'
import { Label, Error, Hint } from '@progress/kendo-react-labels'
import { Upload } from '@progress/kendo-react-upload'
import { DropDownList, AutoComplete, MultiSelect, ComboBox } from '@progress/kendo-react-dropdowns'
import { filterBy } from '@progress/kendo-data-query'
import { Editor, EditorTools, ProseMirror } from '@progress/kendo-react-editor'
import { withTranslation } from 'react-i18next'
import i18n from '../../i18n'
import messageConstant from '../../constants/messageConstant'
import { InsertImage } from './EditorComponent/insertImageTool'
import { InsertImagePlugin } from './EditorComponent/insertImagePlugin'
import { InsertImageFiles } from './EditorComponent/utils'
import '../layoutComponents/ApiLoader/apiLoader.style.scss'

export const FormInput = fieldRenderProps => {
  const {
    validationMessage,
    touched,
    label,
    requiredText,
    id,
    valid,
    disabled,
    hint,
    type,
    optional,
    onBlur,
    leftInputGroup,
    rightInputGroup,
    placeholder,
    format,
    ...others
  } = fieldRenderProps
  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''

  return (
    <FieldWrapper>
      <Label editorId={id} editorValid={valid} editorDisabled={disabled} optional={optional}>
        {i18n.t(label)} <span className="text-danger">{requiredText}</span>
      </Label>
      <div className="k-form-field-wrap">
        <div className="input-group cm-kg-input-group">
          {leftInputGroup && (
            <div className="input-group-prepend">
              <span className="input-group-text" id="kg-search">
                {leftInputGroup}
              </span>
            </div>
          )}
          <Input
            valid={valid}
            type={type}
            id={id}
            disabled={disabled}
            ariaDescribedBy={`${hintId} ${errorId}`}
            onBlur={onBlur}
            placeholder={i18n.t(placeholder)}
            format={format}
            {...others}
          />
          {rightInputGroup && (
            <div className="input-group-append">
              <span className="input-group-text" id="kg-search">
                {rightInputGroup}
              </span>
            </div>
          )}
        </div>
        {showHint && <Hint id={hintId}>{hint}</Hint>}
        {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
      </div>
    </FieldWrapper>
  )
}

export const FormNumericTextBox = fieldRenderProps => {
  const {
    validationMessage,
    touched,
    label,
    requiredText,
    id,
    valid,
    disabled,
    hint,
    type,
    optional,
    onBlur,
    leftInputGroup,
    rightInputGroup,
    placeholder,
    format,
    ...others
  } = fieldRenderProps
  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''

  return (
    <FieldWrapper>
      <Label editorId={id} editorValid={valid} editorDisabled={disabled} optional={optional}>
        {i18n.t(label)} <span className="text-danger">{requiredText}</span>
      </Label>
      <div className="k-form-field-wrap">
        <div className="input-group cm-kg-input-group">
          {leftInputGroup && (
            <div className="input-group-prepend">
              <span className="input-group-text" id="kg-search">
                {leftInputGroup}
              </span>
            </div>
          )}
          <NumericTextBox
            valid={valid}
            type={type}
            id={id}
            disabled={disabled}
            ariaDescribedBy={`${hintId} ${errorId}`}
            onBlur={onBlur}
            placeholder={i18n.t(placeholder)}
            format={format}
            {...others}
          />
          {rightInputGroup && (
            <div className="input-group-append">
              <span className="input-group-text" id="kg-search">
                {rightInputGroup}
              </span>
            </div>
          )}
        </div>
        {showHint && <Hint id={hintId}>{hint}</Hint>}
        {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
      </div>
    </FieldWrapper>
  )
}

export const FormNumericTextBoxWithChangeEvent = fieldRenderProps => {
  const {
    validationMessage,
    touched,
    label,
    requiredText,
    id,
    valid,
    disabled,
    hint,
    type,
    optional,
    onBlur,
    leftInputGroup,
    rightInputGroup,
    placeholder,
    format,
    handleInputChange,
    value,
  } = fieldRenderProps
  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''

  const onInputChange = event => {
    fieldRenderProps.onChange({ target: event.target, value: event.value })
    handleInputChange(event)
  }

  return (
    <FieldWrapper>
      <Label editorId={id} editorValid={valid} editorDisabled={disabled} optional={optional}>
        {i18n.t(label)} <span className="text-danger">{requiredText}</span>
      </Label>
      <div className="k-form-field-wrap">
        <div className="input-group cm-kg-input-group">
          {leftInputGroup && (
            <div className="input-group-prepend">
              <span className="input-group-text" id="kg-search">
                {leftInputGroup}
              </span>
            </div>
          )}
          <NumericTextBox
            valid={valid}
            type={type}
            id={id}
            disabled={disabled}
            ariaDescribedBy={`${hintId} ${errorId}`}
            onBlur={onBlur}
            placeholder={i18n.t(placeholder)}
            format={format}
            onChange={onInputChange}
            value={value}
          />
          {rightInputGroup && (
            <div className="input-group-append">
              <span className="input-group-text" id="kg-search">
                {rightInputGroup}
              </span>
            </div>
          )}
        </div>
        {showHint && <Hint id={hintId}>{hint}</Hint>}
        {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
      </div>
    </FieldWrapper>
  )
}

export const FormInputWithChangeEvent = fieldRenderProps => {
  const {
    validationMessage,
    touched,
    label,
    requiredText,
    id,
    valid,
    disabled,
    hint,
    value,
    type,
    optional,
    onBlur,
    leftInputGroup,
    rightInputGroup1,
    rightInputGroup2,
    handleInputChange,
    maxLength,
    max,
    min,
    placeholder,
    className,
  } = fieldRenderProps
  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''

  const onInputChange = event => {
    fieldRenderProps.onChange({ target: event.target, value: event.value })
    handleInputChange(event)
  }

  return (
    <FieldWrapper>
      <Label editorId={id} editorValid={valid} editorDisabled={disabled} optional={optional}>
        {i18n.t(label)} <span className="text-danger">{requiredText}</span>
      </Label>
      <div className="k-form-field-wrap">
        <div className="input-group cm-kg-input-group">
          {leftInputGroup && (
            <div className="input-group-prepend">
              <span className="input-group-text" id="kg-search">
                {leftInputGroup}
              </span>
            </div>
          )}
          <Input
            className={className}
            valid={valid}
            type={type}
            id={id}
            disabled={disabled}
            placeholder={i18n.t(placeholder)}
            ariaDescribedBy={`${hintId} ${errorId}`}
            onBlur={onBlur}
            onChange={onInputChange}
            value={value}
            maxLength={maxLength}
            max={max}
            min={min}
          />
          <div className="input-group-append">
            {rightInputGroup1 ? (
              <span className="input-group-text" id="kg-search">
                {' '}
                {rightInputGroup1}{' '}
              </span>
            ) : null}
            {rightInputGroup2 ? (
              <span className="input-group-text" id="kg-search">
                {' '}
                {rightInputGroup2}{' '}
              </span>
            ) : null}
          </div>
        </div>
        {showHint && <Hint id={hintId}>{hint}</Hint>}
        {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
      </div>
    </FieldWrapper>
  )
}

export const FormTextArea = fieldRenderProps => {
  const {
    validationMessage,
    touched,
    label,
    requiredText,
    id,
    valid,
    hint,
    optional,
    placeholder,
    visited,
    ...others
  } = fieldRenderProps

  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''

  return (
    <FieldWrapper>
      <Label editorId={id} editorValid={valid} optional={optional}>
        {i18n.t(label)} <span className="text-danger">{requiredText}</span>
      </Label>
      <TextArea
        aria-describedby={`${hintId} ${errorId}`}
        className="k-textarea k-autofill"
        id={id}
        placeholder={i18n.t(placeholder)}
        {...others}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}

export const FormTextAreaWithChangeEvent = fieldRenderProps => {
  const {
    validationMessage,
    touched,
    label,
    requiredText,
    id,
    valid,
    hint,
    optional,
    value,
    handleInputChange,
    maxLength,
    placeholder,
  } = fieldRenderProps

  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''

  const onInputChange = event => {
    fieldRenderProps.onChange({ target: event.target, value: event.value })
    handleInputChange(event)
  }

  return (
    <FieldWrapper>
      <Label editorId={id} editorValid={valid} optional={optional}>
        {i18n.t(label)} <span className="text-danger">{requiredText}</span>
      </Label>
      <TextArea
        aria-describedby={`${hintId} ${errorId}`}
        className="k-textarea k-autofill"
        id={id}
        onChange={onInputChange}
        value={value}
        maxLength={maxLength}
        placeholder={i18n.t(placeholder)}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}

export const FormMaskedTextBox = fieldRenderProps => {
  const {
    validationMessage,
    touched,
    label,
    requiredText,
    id,
    valid,
    hint,
    optional,
    mask,
    placeholder,
    ...others
  } = fieldRenderProps

  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''

  return (
    <FieldWrapper>
      <Label editorId={id} editorValid={valid} optional={optional}>
        {i18n.t(label)} <span className="text-danger">{requiredText}</span>
      </Label>
      <div className="k-form-field-wrap">
        <MaskedTextBox
          ariaDescribedBy={`${hintId} ${errorId}`}
          valid={valid}
          id={id}
          mask={mask}
          placeholder={i18n.t(placeholder)}
          {...others}
        />
        {showHint && <Hint id={hintId}>{hint}</Hint>}
        {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
      </div>
    </FieldWrapper>
  )
}

export const FormCheckbox = fieldRenderProps => {
  const {
    validationMessage,
    touched,
    id,
    valid,
    disabled,
    hint,
    optional,
    label,
    ...others
  } = fieldRenderProps

  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''

  return (
    <FieldWrapper>
      <Checkbox
        ariaDescribedBy={`${hintId} ${errorId}`}
        label={i18n.t(label)}
        labelOptional={optional}
        valid={valid}
        id={id}
        disabled={disabled}
        {...others}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}

export const FormCheckboxWithChangeEvent = fieldRenderProps => {
  const {
    validationMessage,
    touched,
    id,
    valid,
    hint,
    value,
    optional,
    label,
    handleCheckboxChange,
    disabled,
  } = fieldRenderProps

  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''

  const onCheckboxChange = event => {
    fieldRenderProps.onChange({ target: event.target, value: event.value })
    handleCheckboxChange(event.value)
  }

  return (
    <FieldWrapper>
      <Checkbox
        ariaDescribedBy={`${hintId} ${errorId}`}
        label={i18n.t(label)}
        labelOptional={optional}
        valid={valid}
        value={value}
        id={id}
        onChange={onCheckboxChange}
        disabled={disabled}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}

export const FormUpload = fieldRenderProps => {
  const {
    valid,
    value,
    id,
    requiredText,
    optional,
    label,
    hint,
    validationMessage,
    touched,
    allowedExtensions,
    onAddImage,
    onRemoveMedia,
    maxFileSize,
    defaultFiles,
  } = fieldRenderProps
  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''

  const onChangeHandler = event => {
    fieldRenderProps.onChange({ target: event.target, value: event.newState })
    onAddImage(event)
  }
  const onRemoveHandler = event => {
    fieldRenderProps.onChange({ target: event.target, value: event.newState })
    onRemoveMedia(event)
  }
  return (
    <FieldWrapper>
      <Label id={labelId} editorId={id} editorValid={valid} optional={optional}>
        {i18n.t(label)} <span className="text-danger">{requiredText}</span>
      </Label>
      <Upload
        id={id}
        valid={valid}
        autoUpload={false}
        showActionButtons={false}
        multiple={false}
        files={value}
        onAdd={onChangeHandler}
        onRemove={onRemoveHandler}
        ariaDescribedBy={`${hintId} ${errorId}`}
        ariaLabelledBy={labelId}
        restrictions={{
          allowedExtensions,
          maxFileSize,
        }}
        defaultFiles={defaultFiles}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}

export const FormDropDownListDynamic = fieldRenderProps => {
  const {
    validationMessage,
    touched,
    label,
    id,
    requiredText,
    valid,
    disabled,
    hint,
    wrapperStyle,
    name,
    data,
    value,
    dataItemKey,
    textField,
    ...others
  } = fieldRenderProps
  const editorRef = React.useRef(null)

  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''
  const onChangeHandler = event => ({ value: event.newState })

  return (
    <FieldWrapper style={wrapperStyle}>
      <Label
        id={labelId}
        editorRef={editorRef}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
      >
        {i18n.t(label)} <span className="text-danger">{requiredText}</span>
      </Label>
      <DropDownList
        ariaLabelledBy={labelId}
        ariaDescribedBy={`${hintId} ${errorId}`}
        ref={editorRef}
        valid={valid}
        id={id}
        data={data}
        name={name}
        dataItemKey={dataItemKey}
        textField={textField}
        disabled={disabled}
        onChange={onChangeHandler}
        value={value}
        {...others}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}

export const FormDropDownListStatic = fieldRenderProps => {
  const {
    validationMessage,
    touched,
    label,
    id,
    requiredText,
    valid,
    disabled,
    hint,
    wrapperStyle,
    name,
    data,
    ...others
  } = fieldRenderProps
  const editorRef = React.useRef(null)

  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''
  const onChangeHandler = event => ({ value: event.newState })
  return (
    <FieldWrapper style={wrapperStyle}>
      <Label
        id={labelId}
        editorRef={editorRef}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
      >
        {i18n.t(label)} <span className="text-danger">{requiredText}</span>
      </Label>
      <DropDownList
        ariaLabelledBy={labelId}
        ariaDescribedBy={`${hintId} ${errorId}`}
        ref={editorRef}
        valid={valid}
        id={id}
        data={data}
        name={name}
        disabled={disabled}
        onChange={onChangeHandler}
        {...others}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}

export const FormDropDownListDynamicWithOnChange = fieldRenderProps => {
  const {
    validationMessage,
    touched,
    label,
    id,
    requiredText,
    valid,
    disabled,
    hint,
    wrapperStyle,
    name,
    data,
    value,
    dataItemKey,
    textField,
    onChangeCallBack,
  } = fieldRenderProps
  const editorRef = React.useRef(null)

  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''

  const itemRender = (li, itemProps) => {
    if (itemProps && itemProps.dataItem && itemProps.dataItem.isDisabled) {
      const props = {
        ...li.props,
        className: 'k-item k-state-disabled',
      }
      return React.cloneElement(li, props, li.props.children)
    }
    return li
  }

  const onChangeHandler = event => {
    fieldRenderProps.onChange({ target: event.target, value: event.newState })
    onChangeCallBack(event)
  }

  return (
    <FieldWrapper style={wrapperStyle}>
      <Label
        id={labelId}
        editorRef={editorRef}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
      >
        {i18n.t(label)} <span className="text-danger">{requiredText}</span>
      </Label>
      <DropDownList
        ariaLabelledBy={labelId}
        ariaDescribedBy={`${hintId} ${errorId}`}
        ref={editorRef}
        valid={valid}
        id={id}
        data={data}
        name={name}
        dataItemKey={dataItemKey}
        textField={textField}
        disabled={disabled}
        onChange={onChangeHandler}
        value={value}
        itemRender={itemRender}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}

export const FormSwitch = fieldRenderProps => {
  const {
    validationMessage,
    touched,
    label,
    optional,
    id,
    valid,
    disabled,
    hint,
    onPreferenceChange,
    defaultChecked,
  } = fieldRenderProps
  const editorRef = React.useRef(null)

  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''
  const onChangeHandler = event => {
    fieldRenderProps.onChange({ target: event.target, value: event.value })
    onPreferenceChange(event)
  }
  return (
    <FieldWrapper>
      <Label
        id={labelId}
        editorRef={editorRef}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
        optional={optional}
        style={{ paddingBottom: '5px' }}
      >
        {i18n.t(label)}
      </Label>
      <Switch
        ref={editorRef}
        ariaLabelledBy={labelId}
        ariaDescribedBy={`${hintId} ${errorId}`}
        valid={valid}
        id={id}
        disabled={disabled}
        onChange={onChangeHandler}
        defaultChecked={defaultChecked}
      />
      {showHint && (
        <Hint id={hintId} style={{ paddingTop: '5px' }}>
          {i18n.t(hint)}
        </Hint>
      )}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}

export const FormAutoCompleteList = fieldRenderProps => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    hint,
    wrapperStyle,
    name,
    data,
    value,
    dataItemKey,
    textField,
    ...others
  } = fieldRenderProps
  const editorRef = React.useRef(null)

  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''

  return (
    <FieldWrapper style={wrapperStyle}>
      <Label
        id={labelId}
        editorRef={editorRef}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
      >
        {i18n.t(label)}
      </Label>
      <AutoComplete
        ariaLabelledBy={labelId}
        ariaDescribedBy={`${hintId} ${errorId}`}
        ref={editorRef}
        valid={valid}
        id={id}
        data={data}
        name={name}
        dataItemKey={dataItemKey}
        textField={textField}
        disabled={disabled}
        value={value}
        {...others}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}

export const FormEditor = fieldRenderProps => {
  const {
    onChange,
    validationMessage,
    touched,
    label,
    id,
    requiredText,
    valid,
    disabled,
    hint,
    wrapperStyle,
    name,
    value,
  } = fieldRenderProps
  const editorRef = React.useRef(null)

  const editorRefr = fieldRenderProps.editorRef

  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''

  const editorOnChange = event => {
    onChange.call(undefined, { ...event, value: event.html })
  }

  const onMount = event => {
    const styles = `
      p {
        color: #bfc9d4;
      }
      body {
        background-color: #1a1c2d;
      }`
    const iframeDocument = event.dom.ownerDocument
    const style = iframeDocument.createElement('style')
    style.appendChild(iframeDocument.createTextNode(styles))
    iframeDocument.head.appendChild(style)
  }

  const {
    Bold,
    Italic,
    Underline,
    AlignLeft,
    AlignRight,
    AlignCenter,
    Indent,
    Outdent,
    OrderedList,
    UnorderedList,
    Undo,
    Redo,
    Link,
    Unlink,
  } = EditorTools

  return (
    <FieldWrapper style={wrapperStyle}>
      <Label
        id={labelId}
        editorRef={editorRef}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
      >
        {i18n.t(label)} <span className="text-danger">{requiredText}</span>
      </Label>
      <Editor
        tools={[
          [Bold, Italic, Underline],
          [Undo, Redo],
          [Link, Unlink],
          [AlignLeft, AlignCenter, AlignRight],
          [OrderedList, UnorderedList, Indent, Outdent],
        ]}
        contentStyle={{ height: 160 }}
        value={value}
        onChange={editorOnChange}
        name={name}
        ref={editorRefr}
        onMount={onMount}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}

export const FormEditorOnChange = fieldRenderProps => {
  const {
    onChange,
    validationMessage,
    touched,
    label,
    id,
    requiredText,
    valid,
    disabled,
    hint,
    wrapperStyle,
    name,
    value,
    editorRef,
  } = fieldRenderProps
  // const editorRef = React.useRef(null);

  const editorRefr = fieldRenderProps.editorRef

  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''

  //  const editorOnChange = event => {
  //    onChange.call(undefined, { ...event, value: event.html })
  //  }

  const onMount = event => {
    const styles = `
      p {
        color: #bfc9d4;
      }
      body {
        background-color: #1a1c2d;
      }`
    const iframeDocument = event.dom.ownerDocument
    const style = iframeDocument.createElement('style')
    style.appendChild(iframeDocument.createTextNode(styles))
    iframeDocument.head.appendChild(style)
  }

  const {
    Bold,
    Italic,
    Underline,
    AlignLeft,
    AlignRight,
    AlignCenter,
    Indent,
    Outdent,
    OrderedList,
    UnorderedList,
    Undo,
    Redo,
    Link,
    Unlink,
  } = EditorTools

  return (
    <FieldWrapper style={wrapperStyle}>
      <Label
        id={labelId}
        editorRef={editorRef}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
      >
        {i18n.t(label)} <span className="text-danger">{requiredText}</span>
      </Label>
      <Editor
        tools={[
          [Bold, Italic, Underline],
          [Undo, Redo],
          [Link, Unlink],
          [AlignLeft, AlignCenter, AlignRight],
          [OrderedList, UnorderedList, Indent, Outdent],
        ]}
        contentStyle={{ height: 160 }}
        value={value}
        onChange={onChange}
        name={name}
        ref={editorRefr}
        onMount={onMount}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}

export const filterData = (searchedValue, dataList) => {
  const data = dataList
  const filter = {
    value: searchedValue,
    field: 'label',
    operator: 'contains',
    ignoreCase: true,
  }
  return searchedValue ? filterBy(data, filter) : data
}

export const FormDatePicker = fieldRenderProps => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    hint,
    wrapperStyle,
    hintDirection,
    requiredText,
    ...others
  } = fieldRenderProps

  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''

  return (
    <FieldWrapper style={wrapperStyle}>
      <Label id={labelId} editorId={id} editorValid={valid} editorDisabled={disabled}>
        {i18n.t(label)} <span className="text-danger">{requiredText}</span>
      </Label>
      <div className="k-form-field-wrap">
        <DatePicker
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          valid={valid}
          id={id}
          disabled={disabled}
          {...others}
        />
        {showHint && (
          <Hint id={hintId} direction={hintDirection}>
            {hint}
          </Hint>
        )}
        {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
      </div>
    </FieldWrapper>
  )
}

export const FormDatePickerWithChangeEvent = fieldRenderProps => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    hint,
    wrapperStyle,
    hintDirection,
    requiredText,
    value,
    handleInputChange,
  } = fieldRenderProps

  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''

  const onInputChange = event => {
    fieldRenderProps.onChange({ target: event.target, value: event.value })
    handleInputChange(event)
  }
  return (
    <FieldWrapper style={wrapperStyle}>
      <Label id={labelId} editorId={id} editorValid={valid} editorDisabled={disabled}>
        {i18n.t(label)} <span className="text-danger">{requiredText}</span>
      </Label>
      <div className="k-form-field-wrap">
        <DatePicker
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          valid={valid}
          id={id}
          disabled={disabled}
          value={value}
          onChange={onInputChange}
        />
        {showHint && (
          <Hint id={hintId} direction={hintDirection}>
            {hint}
          </Hint>
        )}
        {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
      </div>
    </FieldWrapper>
  )
}

export const FormDateTimePicker = fieldRenderProps => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    hint,
    wrapperStyle,
    hintDirection,
    requiredText,
    ...others
  } = fieldRenderProps

  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''

  return (
    <FieldWrapper style={wrapperStyle}>
      <Label id={labelId} editorId={id} editorValid={valid} editorDisabled={disabled}>
        {i18n.t(label)} <span className="text-danger">{requiredText}</span>
      </Label>
      <div className="k-form-field-wrap">
        <DateTimePicker
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          valid={valid}
          id={id}
          disabled={disabled}
          {...others}
        />
        {showHint && (
          <Hint id={hintId} direction={hintDirection}>
            {hint}
          </Hint>
        )}
        {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
      </div>
    </FieldWrapper>
  )
}

export const FormDateTimePickerWithChangeEvent = fieldRenderProps => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    hint,
    wrapperStyle,
    requiredText,
    value,
    handleInputChange,
  } = fieldRenderProps

  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''

  const onInputChange = event => {
    fieldRenderProps.onChange({ target: event.target, value: event.value })
    handleInputChange(event)
  }

  return (
    <FieldWrapper style={wrapperStyle}>
      <Label id={labelId} editorId={id} editorValid={valid} editorDisabled={disabled}>
        {i18n.t(label)} <span className="text-danger">{requiredText}</span>
      </Label>
      <DateTimePicker
        ariaLabelledBy={labelId}
        ariaDescribedBy={`${hintId} ${errorId}`}
        valid={valid}
        id={id}
        disabled={disabled}
        value={value}
        onChange={onInputChange}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}

export const FormMultiSelect = fieldRenderProps => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    hint,
    value,
    wrapperStyle,
    requiredText,
    placeholder,
    ...others
  } = fieldRenderProps
  const editorRef = React.useRef(null)

  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''
  const itemRender = (li, itemProps) => {
    if (itemProps && itemProps.dataItem && itemProps.dataItem.isDisabled) {
      const props = {
        ...li.props,
        className: 'k-item k-state-disabled',
      }
      return React.cloneElement(li, props, li.props.children)
    }
    return li
  }
  return (
    <FieldWrapper style={wrapperStyle}>
      <Label
        id={labelId}
        editorRef={editorRef}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
      >
        {i18n.t(label)} <span className="text-danger">{requiredText}</span>
      </Label>
      <MultiSelect
        ariaLabelledBy={labelId}
        ariaDescribedBy={`${hintId} ${errorId}`}
        ref={editorRef}
        itemRender={itemRender}
        valid={valid}
        id={id}
        disabled={disabled}
        value={value}
        placeholder={i18n.t(placeholder)}
        {...others}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}

export const FormMultiSelectWithChangeEvent = fieldRenderProps => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    hint,
    wrapperStyle,
    requiredText,
    placeholder,
    handleMultiSelectChange,
    data,
    value,
    dataItemKey,
    textField,
    name,
    className,
  } = fieldRenderProps
  const editorRef = React.useRef(null)

  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''

  const onMultiSelectChange = event => {
    const selectedValue = event.target.value
    if (selectedValue && !selectedValue.isDisabled) {
      fieldRenderProps.onChange({ target: event.target, value: event.value })
    } else {
      fieldRenderProps.onChange({ target: event.target, value: '' })
    }
    handleMultiSelectChange(event.value)
  }

  const itemRender = (li, itemProps) => {
    if (itemProps && itemProps.dataItem && itemProps.dataItem.isDisabled) {
      const props = {
        ...li.props,
        className: 'k-item k-state-disabled',
      }
      return React.cloneElement(li, props, li.props.children)
    }
    return li
  }

  return (
    <FieldWrapper style={wrapperStyle}>
      <Label
        id={labelId}
        editorRef={editorRef}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
      >
        {i18n.t(label)} <span className="text-danger">{requiredText}</span>
      </Label>
      <MultiSelect
        ariaLabelledBy={labelId}
        ariaDescribedBy={`${hintId} ${errorId}`}
        ref={editorRef}
        valid={valid}
        id={id}
        data={data}
        itemRender={itemRender}
        name={name}
        dataItemKey={dataItemKey}
        textField={textField}
        disabled={disabled}
        value={value}
        onChange={onMultiSelectChange}
        placeholder={i18n.t(placeholder)}
        filterable={false}
        className={className}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}

export const FormComboBoxList = fieldRenderProps => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    requiredText,
    hint,
    wrapperStyle,
    name,
    data,
    value,
    dataItemKey,
    textField,
    handleFilter,
    onChange,
    placeholder,
    ...others
  } = fieldRenderProps
  const editorRef = React.useRef(null)

  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''
  const isFilterable = true

  const filterChange = event => {
    const dataListToFilter = data.orignalList.slice()
    const result = filterBy(dataListToFilter, event.filter)
    result.sort((a, b) => a.isDisabled - b.isDisabled)
    handleFilter(result)
  }

  const itemRender = (li, itemProps) => {
    if (itemProps && itemProps.dataItem && itemProps.dataItem.isDisabled) {
      const props = {
        ...li.props,
        className: 'k-item k-state-disabled',
      }
      return React.cloneElement(li, props, li.props.children)
    }
    return li
  }

  const changeHandler = event => {
    const selectedValue = event.target.value
    if (selectedValue && !selectedValue.isDisabled) {
      fieldRenderProps.onChange({ target: event.target, value: event.value })
    } else {
      fieldRenderProps.onChange({ target: event.target, value: '' })
    }
  }

  return (
    <FieldWrapper style={wrapperStyle}>
      <Label
        id={labelId}
        editorRef={editorRef}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
      >
        {i18n.t(label)} <span className="text-danger">{requiredText}</span>
      </Label>
      <ComboBox
        ariaLabelledBy={labelId}
        ariaDescribedBy={`${hintId} ${errorId}`}
        ref={editorRef}
        valid={valid}
        id={id}
        data={data.filteredList}
        itemRender={itemRender}
        name={name}
        dataItemKey={dataItemKey}
        textField={textField}
        filterable={isFilterable}
        suggest={isFilterable}
        onFilterChange={filterChange}
        disabled={disabled}
        value={value}
        onChange={changeHandler}
        placeholder={i18n.t(messageConstant.TypeToSearch)}
        {...others}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}

export const FormComboBoxListWithChangeEvent = fieldRenderProps => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    requiredText,
    hint,
    wrapperStyle,
    name,
    data,
    value,
    dataItemKey,
    textField,
    handleFilter,
    handleComboBoxChange,
    clearButton,
  } = fieldRenderProps
  const editorRef = React.useRef(null)

  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''
  const isFilterable = true

  const filterChange = event => {
    const dataListToFilter = data.orignalList.slice()
    const result = filterBy(dataListToFilter, event.filter)
    result.sort((a, b) => a.isDisabled - b.isDisabled)
    handleFilter(result)
  }

  const itemRender = (li, itemProps) => {
    if (itemProps && itemProps.dataItem && itemProps.dataItem.isDisabled) {
      const props = {
        ...li.props,
        className: 'k-item k-state-disabled',
      }
      return React.cloneElement(li, props, li.props.children)
    }
    return li
  }

  const onComboBoxChange = event => {
    const selectedValue = event.target.value
    if (selectedValue && !selectedValue.isDisabled) {
      fieldRenderProps.onChange({ target: event.target, value: event.value })
    } else {
      fieldRenderProps.onChange({ target: event.target, value: '' })
    }
    handleComboBoxChange(event.value)
  }

  return (
    <FieldWrapper style={wrapperStyle}>
      <Label
        id={labelId}
        editorRef={editorRef}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
      >
        {i18n.t(label)} <span className="text-danger">{requiredText}</span>
      </Label>
      <ComboBox
        ariaLabelledBy={labelId}
        ariaDescribedBy={`${hintId} ${errorId}`}
        ref={editorRef}
        valid={valid}
        id={id}
        data={data.filteredList}
        itemRender={itemRender}
        name={name}
        dataItemKey={dataItemKey}
        textField={textField}
        filterable={isFilterable}
        suggest={isFilterable}
        onFilterChange={filterChange}
        disabled={disabled}
        value={value}
        onChange={onComboBoxChange}
        placeholder={i18n.t(messageConstant.TypeToSearch)}
        clearButton={clearButton}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}

export const FormComponentSectionLoader = fieldRenderProps => {
  const { id, className } = fieldRenderProps
  return (
    <FieldWrapper className={className}>
      <div className="d-flex justify-content-between mx-auto mt-4">
        <div
          id={id}
          className="spinner-border text-loader-info align-self-center loader-lg mx-auto"
        />
      </div>
    </FieldWrapper>
  )
}

export const FormComponentInputControlLoader = fieldRenderProps => {
  const { id, className } = fieldRenderProps
  return (
    <FieldWrapper className={className}>
      <div className="d-flex justify-content-between mx-auto">
        <div
          id={id}
          className="spinner-border text-loader-info align-self-center loader-lg cm-loader-25 ml-auto"
        />
      </div>
    </FieldWrapper>
  )
}

export const FormEditorWithImage = fieldRenderProps => {
  const {
    onChange,
    validationMessage,
    touched,
    label,
    id,
    requiredText,
    valid,
    disabled,
    hint,
    wrapperStyle,
    name,
    value,
  } = fieldRenderProps
  const editorRef = React.useRef(null)

  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''

  const editorOnChange = event => {
    onChange.call(undefined, { ...event, value: event.html })
  }

  const onImageInsert = args => {
    const { files, view, event } = args
    const nodeType = view.state.schema.nodes.image
    const position =
      event.type === 'drop'
        ? view.posAtCoords({
            left: event.clientX,
            top: event.clientY,
          })
        : null
    InsertImageFiles({
      view,
      files,
      nodeType,
      position,
    })
    return files.length > 0
  }

  const onMount = event => {
    const { state } = event.viewProps
    const plugins = [...state.plugins, InsertImagePlugin(onImageInsert)]

    const styles = `
      p {
        color: #bfc9d4;
      }
      body {
        background-color: #1a1c2d;
      }`
    const iframeDocument = event.dom.ownerDocument
    const style = iframeDocument.createElement('style')
    style.appendChild(iframeDocument.createTextNode(styles))
    iframeDocument.head.appendChild(style)

    return new ProseMirror.EditorView(
      {
        mount: event.dom,
      },
      {
        ...event.viewProps,
        state: ProseMirror.EditorState.create({
          doc: state.doc,
          plugins,
        }),
      },
    )
  }

  const {
    Bold,
    Italic,
    Underline,
    AlignLeft,
    AlignRight,
    AlignCenter,
    Indent,
    Outdent,
    OrderedList,
    UnorderedList,
    Undo,
    Redo,
    Link,
    Unlink,
  } = EditorTools

  return (
    <FieldWrapper style={wrapperStyle}>
      <Label
        id={labelId}
        editorRef={editorRef}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
      >
        {i18n.t(label)} <span className="text-danger">{requiredText}</span>
      </Label>
      <Editor
        tools={[
          [Bold, Italic, Underline],
          [Undo, Redo],
          [Link, Unlink],
          [AlignLeft, AlignCenter, AlignRight],
          [OrderedList, UnorderedList, Indent, Outdent],
          [InsertImage],
        ]}
        contentStyle={{ height: 160 }}
        value={value}
        onChange={editorOnChange}
        name={name}
        onMount={onMount}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}

export default withTranslation()({
  FormInput,
  FormInputWithChangeEvent,
  FormNumericTextBox,
  FormNumericTextBoxWithChangeEvent,
  FormMaskedTextBox,
  FormCheckbox,
  FormCheckboxWithChangeEvent,
  FormUpload,
  FormDropDownListDynamic,
  FormDropDownListStatic,
  FormSwitch,
  FormAutoCompleteList,
  filterData,
  FormEditor,
  FormDatePicker,
  FormDatePickerWithChangeEvent,
  FormDateTimePicker,
  FormDateTimePickerWithChangeEvent,
  FormMultiSelect,
  FormMultiSelectWithChangeEvent,
  FormComboBoxList,
  FormComboBoxListWithChangeEvent,
  FormEditorWithImage,
  FormEditorOnChange,
})
